import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Scales, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { newClauseId, TNC_MODES } from "../builderStorage";

/**
 * Section 5 — Terms & Conditions.
 *
 * Three modes:
 *   - full          — pick an existing T&C set from the master library
 *   - reference     — type a reference proposal/agreement number + note
 *   - reference_plus_additional — both reference + a list of additional clauses
 *
 * T&C set list is read-only from /api/billing/proposal-tnc/list.
 * No writes to the master library from this prototype.
 */
export default function TermsAndConditionsSection({ tnc, onChange }) {
    const [tncSets, setTncSets] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        async function fetchSets() {
            try {
                const { data } = await api.get("/billing/proposal-tnc/list?for_picker=1");
                if (mounted) {
                    const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
                    setTncSets(arr);
                }
            } catch {
                if (mounted) toast.error("Could not load T&C sets. Reference modes still available.");
            } finally {
                if (mounted) setLoading(false);
            }
        }
        fetchSets();
        return () => { mounted = false; };
    }, []);

    function setMode(mode) {
        onChange({ ...tnc, mode });
    }

    function addClause() {
        onChange({
            ...tnc,
            additionalClauses: [
                ...(tnc.additionalClauses || []),
                { id: newClauseId(), title: "", body: "" },
            ],
        });
    }
    function updateClause(id, patch) {
        onChange({
            ...tnc,
            additionalClauses: (tnc.additionalClauses || []).map((c) => (c.id === id ? { ...c, ...patch } : c)),
        });
    }
    function removeClause(id) {
        onChange({
            ...tnc,
            additionalClauses: (tnc.additionalClauses || []).filter((c) => c.id !== id),
        });
    }

    const fullSet = tncSets.find((s) => s.id === tnc.fullTncId) || null;
    const showReferenceFields = tnc.mode === "reference" || tnc.mode === "reference_plus_additional";
    const showAdditionalClauses = tnc.mode === "reference_plus_additional";

    return (
        <Card className="border-slate-200" data-testid="section-tnc">
            <CardHeader>
                <CardTitle className="flex items-center gap-2"><Scales weight="duotone" /> Section 4 — Terms & Conditions</CardTitle>
                <CardDescription>
                    Choose Full (complete T&C in this proposal) or Reference (carry T&C from an earlier proposal/agreement, optionally with additional clauses).
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
                <RadioGroup
                    value={tnc.mode}
                    onValueChange={setMode}
                    className="grid md:grid-cols-3 gap-2"
                    data-testid="tnc-mode-group"
                >
                    {TNC_MODES.map((m) => (
                        <Label
                            key={m.code}
                            htmlFor={`tnc-${m.code}`}
                            className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer transition ${
                                tnc.mode === m.code
                                    ? "bg-indigo-50 border-indigo-300"
                                    : "border-slate-200 hover:bg-slate-50"
                            }`}
                        >
                            <RadioGroupItem value={m.code} id={`tnc-${m.code}`} data-testid={`tnc-mode-${m.code}`} />
                            <div>
                                <div className="text-sm font-semibold">{m.label}</div>
                                <div className="text-xs text-slate-500">{m.description}</div>
                            </div>
                        </Label>
                    ))}
                </RadioGroup>

                {tnc.mode === "full" && (
                    <div className="space-y-3" data-testid="tnc-full-block">
                        <Label className="text-xs uppercase tracking-wide text-slate-500">Select master T&C set</Label>
                        <Select
                            value={tnc.fullTncId || ""}
                            onValueChange={(v) => onChange({ ...tnc, fullTncId: v })}
                        >
                            <SelectTrigger data-testid="tnc-set-select">
                                <SelectValue placeholder={loading ? "Loading…" : "Choose a T&C set"} />
                            </SelectTrigger>
                            <SelectContent>
                                {tncSets.map((s) => (
                                    <SelectItem key={s.id} value={s.id}>
                                        {s.name} {s.is_default ? "(Default)" : ""}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {fullSet && (
                            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5">
                                <div className="font-semibold text-slate-700">{fullSet.name}</div>
                                {fullSet.description && <div className="text-slate-600">{fullSet.description}</div>}
                                <div className="text-slate-500">
                                    {fullSet.clauses?.length || 0} clauses · {fullSet.payment_terms?.length || 0} payment terms
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {showReferenceFields && (
                    <div className="space-y-3" data-testid="tnc-reference-block">
                        <div>
                            <Label className="text-xs uppercase tracking-wide text-slate-500">Reference proposal / agreement number</Label>
                            <Input
                                value={tnc.referenceProposalNumber || ""}
                                onChange={(e) => onChange({ ...tnc, referenceProposalNumber: e.target.value })}
                                placeholder="e.g. REE/MFBO/0042/2024-25 or MSA-XYZ-2024-01"
                                data-testid="tnc-reference-number"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wide text-slate-500">Reference note (optional — overrides default wording)</Label>
                            <Textarea
                                value={tnc.referenceNote || ""}
                                onChange={(e) => onChange({ ...tnc, referenceNote: e.target.value })}
                                placeholder={`The Terms & Conditions contained in ${tnc.referenceProposalNumber || "[Reference Number]"} shall continue to apply unless specifically modified in this proposal.`}
                                rows={3}
                                data-testid="tnc-reference-note"
                            />
                        </div>
                    </div>
                )}

                {showAdditionalClauses && (
                    <div className="space-y-2" data-testid="tnc-additional-clauses-block">
                        <div className="flex items-center justify-between">
                            <Label className="text-xs uppercase tracking-wide text-slate-500">Additional clauses</Label>
                            <Button size="sm" variant="outline" onClick={addClause} data-testid="tnc-add-clause-btn">
                                <Plus className="mr-1" /> Add clause
                            </Button>
                        </div>
                        {(tnc.additionalClauses || []).length === 0 && (
                            <div className="text-xs text-slate-500 italic">
                                No additional clauses yet.
                            </div>
                        )}
                        {(tnc.additionalClauses || []).map((c, idx) => (
                            <div key={c.id} className="border border-slate-200 rounded p-3 space-y-2" data-testid={`tnc-clause-${c.id}`}>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-slate-500">Clause {idx + 1}</span>
                                    <Input
                                        value={c.title}
                                        onChange={(e) => updateClause(c.id, { title: e.target.value })}
                                        placeholder="Clause title"
                                        className="flex-1"
                                        data-testid={`tnc-clause-title-${c.id}`}
                                    />
                                    <Button size="sm" variant="ghost" onClick={() => removeClause(c.id)} data-testid={`tnc-clause-remove-${c.id}`}>
                                        <X />
                                    </Button>
                                </div>
                                <Textarea
                                    value={c.body}
                                    onChange={(e) => updateClause(c.id, { body: e.target.value })}
                                    placeholder="Clause body"
                                    rows={3}
                                    data-testid={`tnc-clause-body-${c.id}`}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
